import { useEffect, useState } from 'react'
import { Loader2, ShieldCheck, Trash2 } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import SteamIcon from '@/components/site/SteamIcon'
import type { Member } from '@/components/members/MemberCard'
import { useAuth, isManager } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface Note {
  id: number
  body: string
  author_id: string
  author_name: string
  author_avatar: string
  created_at: number
}

export interface SteamChange {
  steam_linked: boolean
  steam_id: string
  steam_name: string
  steam_verified: boolean
}

/**
 * Staff-only panel for one member: hand-entered SteamID64 and the notes log.
 *
 * Both halves are gated on the backend (`require_operator`); this dialog is
 * only ever opened from a card that already knows the viewer is staff.
 */
export default function MemberManageDialog({ member, onClose, onSteamChanged, onNotesChanged }: {
  member: Member | null
  onClose: () => void
  onSteamChanged: (id: string, change: SteamChange) => void
  onNotesChanged: (id: string, count: number) => void
}) {
  const { user } = useAuth()
  const { T, lang } = useLanguage()
  const t = T.members.manage

  const [steamInput, setSteamInput] = useState('')
  const [steamBusy, setSteamBusy] = useState(false)
  const [steamMsg, setSteamMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const [notes, setNotes] = useState<Note[]>([])
  const [notesLoading, setNotesLoading] = useState(false)
  const [notesError, setNotesError] = useState('')
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)

  const memberId = member?.id

  useEffect(() => {
    if (!memberId) return
    setSteamInput(member?.steam_id ?? '')
    setSteamMsg(null)
    setDraft('')
    setNotes([])
    setNotesError('')
    setNotesLoading(true)
    let cancelled = false
    fetch(`/api/members/${memberId}/notes`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then((rows: Note[]) => { if (!cancelled) setNotes(rows) })
      .catch(() => { if (!cancelled) setNotesError(t.notesLoadError) })
      .finally(() => { if (!cancelled) setNotesLoading(false) })
    return () => { cancelled = true }
    // Reload only when a different member is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId])

  if (!member) return null

  async function saveSteam(value: string) {
    if (!member) return
    setSteamBusy(true)
    setSteamMsg(null)
    try {
      const res = await fetch(`/api/members/${member.id}/steam`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setSteamMsg({
          ok: false,
          text: data.detail === 'steam_taken' ? t.steamTaken
            : data.detail === 'invalid_steam_id' ? t.steamInvalid
            : t.steamError,
        })
        return
      }
      const change: SteamChange = {
        steam_linked: data.steam_linked,
        steam_id: data.steam_id,
        steam_name: data.steam_name,
        steam_verified: data.steam_verified,
      }
      setSteamInput(change.steam_id)
      onSteamChanged(member.id, change)
      setSteamMsg({ ok: true, text: change.steam_linked ? t.steamSaved : t.steamCleared })
    } catch {
      setSteamMsg({ ok: false, text: t.steamError })
    } finally {
      setSteamBusy(false)
    }
  }

  async function postNote() {
    if (!member || !draft.trim()) return
    setPosting(true)
    setNotesError('')
    try {
      const res = await fetch(`/api/members/${member.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: draft }),
      })
      if (!res.ok) throw new Error()
      const note: Note = await res.json()
      const next = [note, ...notes]
      setNotes(next)
      setDraft('')
      onNotesChanged(member.id, next.length)
    } catch {
      setNotesError(t.noteSaveError)
    } finally {
      setPosting(false)
    }
  }

  async function removeNote(id: number) {
    if (!member || !confirm(t.noteDeleteConfirm)) return
    const res = await fetch(`/api/members/notes/${id}`, { method: 'DELETE' })
    if (!res.ok) { setNotesError(t.noteDeleteError); return }
    const next = notes.filter(n => n.id !== id)
    setNotes(next)
    onNotesChanged(member.id, next.length)
  }

  const canDelete = (n: Note) => n.author_id === user?.id || isManager(user)
  const fmt = (ts: number) => new Date(ts * 1000).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  const steamDirty = steamInput.trim() !== (member.steam_id ?? '')

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <img
              src={member.avatar_url}
              alt=""
              className="h-8 w-8 rounded-full object-cover"
              onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
            />
            <span className="truncate">{member.display_name}</span>
          </DialogTitle>
          <DialogDescription>{t.staffOnly}</DialogDescription>
        </DialogHeader>

        {/* SteamID64 */}
        <section className="space-y-2">
          <h4 className="border-l-2 border-primary pl-2 font-display text-xs uppercase tracking-[0.18em]">
            {t.steamTitle}
          </h4>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <SteamIcon size={12} />
            {member.steam_linked ? (
              <>
                <span className="truncate">{member.steam_name || T.members.steamNoName}</span>
                {member.steam_verified
                  ? <ShieldCheck className="h-3 w-3 shrink-0 text-primary" aria-label={T.members.steamVerified} />
                  : <span className="text-[10px] uppercase tracking-wider text-amber-400">{T.members.steamUnverified}</span>}
              </>
            ) : <span>{T.members.steamNotLinked}</span>}
          </div>
          <form
            className="flex gap-2"
            onSubmit={e => { e.preventDefault(); void saveSteam(steamInput) }}
          >
            <Input
              value={steamInput}
              onChange={e => setSteamInput(e.target.value)}
              placeholder={t.steamPlaceholder}
              className="font-mono text-xs"
              disabled={steamBusy}
            />
            <Button type="submit" size="sm" disabled={steamBusy || !steamDirty || !steamInput.trim()}>
              {steamBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t.steamSave}
            </Button>
            {member.steam_linked && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                disabled={steamBusy}
                onClick={() => { if (confirm(t.steamClearConfirm)) void saveSteam('') }}
              >
                {t.steamClear}
              </Button>
            )}
          </form>
          <p className="text-[11px] text-muted-foreground/70">{t.steamHint}</p>
          {steamMsg && (
            <p className={`text-xs ${steamMsg.ok ? 'text-emerald-400' : 'text-destructive'}`}>{steamMsg.text}</p>
          )}
        </section>

        {/* Notes */}
        <section className="space-y-3">
          <h4 className="border-l-2 border-primary pl-2 font-display text-xs uppercase tracking-[0.18em]">
            {t.notesTitle}
          </h4>
          <div className="space-y-2">
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void postNote() }
              }}
              maxLength={2000}
              rows={3}
              placeholder={t.notePlaceholder}
              className="w-full resize-y rounded-sm border border-white/10 bg-white/5 px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-white/20"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={() => void postNote()} disabled={posting || !draft.trim()}>
                {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t.noteAdd}
              </Button>
            </div>
          </div>

          {notesError && <p className="text-xs text-destructive">{notesError}</p>}

          {notesLoading ? (
            <p className="text-xs text-muted-foreground">{T.members.loading}</p>
          ) : notes.length === 0 ? (
            <p className="text-xs text-muted-foreground/70">{t.notesEmpty}</p>
          ) : (
            <ul className="space-y-2">
              {notes.map(n => (
                <li key={n.id} className="rounded-sm border border-white/10 bg-white/[0.035] px-3 py-2">
                  <div className="mb-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                    {n.author_avatar && <img src={n.author_avatar} alt="" className="h-4 w-4 rounded-full" />}
                    <span className="font-medium text-foreground/80">{n.author_name}</span>
                    <span>·</span>
                    <span>{fmt(n.created_at)}</span>
                    {canDelete(n) && (
                      <button
                        type="button"
                        onClick={() => void removeNote(n.id)}
                        className="ml-auto text-muted-foreground/60 transition-colors hover:text-destructive"
                        aria-label={t.noteDelete}
                        title={t.noteDelete}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm">{n.body}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </DialogContent>
    </Dialog>
  )
}
